<script setup lang="ts">
import { ClaimCostCharges, ClaimCostPayableTo, type IClaimCostsForm, type IClaimDashboard } from '@/types/asicw/claims'
import { Delete } from '@element-plus/icons-vue'
import type { FormInstance } from 'element-plus'
import { reactive, ref } from 'vue'

const emit = defineEmits(['submit'])

interface ClaimCostsFormProps {
  claim: IClaimDashboard
}

const props = defineProps<ClaimCostsFormProps>()

const formRef = ref<FormInstance>()
const form = reactive<IClaimCostsForm>({
  costs: [],
})

const addCost = () => {
  form.costs.push({
    key: Date.now(),
    charge: '',
    cost: 0,
    payable_from: '',
    payable_to: '',
  })
}

const removeCost = (cost: (typeof form.costs)[number]) => {
  const index = form.costs.indexOf(cost)
  if (index !== -1) {
    form.costs.splice(index, 1)
  }
}

/**
 * Returns the elem that follows `current` in the cycled `array`.
 */
const cycle = <T,>(array: Readonly<T[]>, current: string): T => {
  const index = array.indexOf(current as unknown as T)
  if (index === -1) return array[0]
  return array[(index + 1) % array.length]
}

const onChargeKey = (event: KeyboardEvent, cost: (typeof form.costs)[number]) => {
  switch (event.key.toLowerCase()) {
    case 'd':
      cost.charge = ClaimCostCharges.Deductible
      break
    case 'l':
      cost.charge = ClaimCostCharges.Labor
      break
    case 'p':
      cost.charge = ClaimCostCharges.Parts
      break
    case 'r':
      cost.charge = cycle([ClaimCostCharges.Repair, ClaimCostCharges.Replacement], cost.charge)
      break
    case 'i':
      cost.charge = ClaimCostCharges.InboundShipping
      break
    case 'o':
      cost.charge = ClaimCostCharges.OutboundShipping
      break
  }
}

const onPayableKey = (
  event: KeyboardEvent,
  cost: (typeof form.costs)[number],
  field: 'payable_from' | 'payable_to'
) => {
  switch (event.key.toLowerCase()) {
    case 'a':
      cost[field] = ClaimCostPayableTo.Administrator
      break
    case 'c':
      cost[field] = ClaimCostPayableTo.Customer
      break
    case 's':
      cost[field] = ClaimCostPayableTo.Servicer
      break
  }
}

defineExpose({
  init: () => {
    // Workaround: Element Plus doesn't seem to handle dynamic form fields
    props.claim.costs?.details.forEach((costDetail) => {
      form.costs.push({
        key: costDetail.id,
        charge: costDetail.charge,
        cost: costDetail.cost,
        payable_from: costDetail.payable_from,
        payable_to: costDetail.payable_to,
      })
    })

    // If no costs exist yet, add an empty one
    if (form.costs.length === 0) {
      addCost()
    }
  },
  submit: () => {
    if (!formRef.value) return

    formRef.value?.validate((valid: boolean) => {
      if (!valid) {
        return
      }

      emit('submit', {
        claimNumber: props.claim.number,
        form: form,
      })
    })
  },
  resetFields: () => {
    form.costs = []
  },
})
</script>

<template>
  <el-form ref="formRef" :model="form" :hide-required-asterisk="true" label-position="top" :label-width="80">
    <!-- Costs creation -->
    <div class="w-full grid grid-cols-[180px_1fr_1fr_1fr_24px] gap-4 align-items-center">
      <span class="translate-y-2">Charge</span>
      <span class="translate-y-2">Cost</span>
      <span class="translate-y-2">Payable From</span>
      <span class="translate-y-2">Payable To</span>
      <!-- Empty element for grid padding -->
      <span />
      <template v-for="(cost, index) in form.costs" :key="cost.key">
        <el-form-item
          :prop="`costs.${index}.charge`"
          :rules="{
            required: true,
            message: 'Charge is required.',
            trigger: 'change',
          }"
        >
          <el-select
            ref="chargesSelectRef"
            v-model="cost.charge"
            data-testid="asicw-claim-cost-select-charge"
            class="w-45"
            placeholder="Charge"
            @keyup="onChargeKey($event, cost)"
          >
            <el-option v-for="opt in Object.values(ClaimCostCharges)" :key="opt" :value="opt">
              <span class="underline">{{ opt.charAt(0) }}</span
              >{{ opt.slice(1) }}
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item
          :prop="`costs.${index}.cost`"
          :rules="{
            required: true,
            message: 'Cost is required.',
            trigger: 'blur',
          }"
        >
          <ElCurrencyInput v-model="cost.cost" clearable data-testid="asicw-claim-cost-input-cost" />
        </el-form-item>
        <el-form-item
          :prop="`costs.${index}.payable_from`"
          :rules="{
            required: true,
            message: 'Payable From is required.',
            trigger: 'change',
          }"
        >
          <el-select
            v-model="cost.payable_from"
            data-testid="asicw-claim-cost-select-from"
            placeholder="Payable From"
            @keyup="onPayableKey($event, cost, 'payable_from')"
          >
            <el-option v-for="opt in Object.values(ClaimCostPayableTo)" :key="opt" :value="opt">
              <span class="underline">{{ opt.charAt(0) }}</span
              >{{ opt.slice(1) }}
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item
          :prop="`costs.${index}.payable_to`"
          :rules="{
            required: true,
            message: 'Payable To is required.',
            trigger: 'change',
          }"
        >
          <el-select
            v-model="cost.payable_to"
            placeholder="Payable To"
            data-testid="asicw-claim-cost-select-to"
            @keyup="onPayableKey($event, cost, 'payable_to')"
          >
            <el-option v-for="opt in Object.values(ClaimCostPayableTo)" :key="opt" :value="opt">
              <span class="underline">{{ opt.charAt(0) }}</span
              >{{ opt.slice(1) }}
            </el-option>
          </el-select>
        </el-form-item>
        <div class="inline-flex justify-center items-center">
          <el-button link :icon="Delete" @click="removeCost(cost)" />
        </div>
      </template>
      <el-button class="self-start justify-self-start" link type="primary" @click="addCost">+ Add cost</el-button>
    </div>
  </el-form>
</template>

<style scoped lang="scss">
:deep(.el-form-item) {
  margin-bottom: 0;
}
</style>
